import { useContext, useEffect, useRef, useState } from "react";
import * as L from "./layout.styles";
import styles from "./index.module.sass";
import {
  IChoiceGroupOption,
  TextField,
  ChoiceGroup,
  Stack,
  SpinnerSize,
  Spinner,
  Dropdown,
  IDropdownOption,
  DetailsList,
  SelectionMode,
  MessageBar,
  MessageBarType,
} from "@fluentui/react";
import {
  Redirect,
  RouteComponentProps,
  useHistory,
  useLocation,
} from "react-router-dom";
import { InvestmentFundFinder } from "./components/InvestmentFundFinder";
import fundService, {
  TransferEstimationDates,
  TransferSimulationManagerInfo,
} from "../../services/FundService";
import { ThemeContext } from "@fluentui/react-theme-provider";
import Auth from "../../Auth/Auth";
import { useForm, Controller } from "react-hook-form";
import OrderService, { OrderType } from "@/services/OrderService";
import FinametrixService from "@/services/FinametrixService";
import React from "react";
import DocumentTitle from "react-document-title";
import { TransferTimeLine } from "./components/TransferTimeLine";
import {
  currencyFormatter,
  decimalFormatter,
  roundToDecimalPlaces,
} from "@/utils/numberFormatter";
import { ReactComponent as IncrementIcon } from "@/images/increment.svg";
import {
  InternalSwitchCardContainer,
  InternalSwitchCardTitle,
  InternalSwitchCardContent,
  InternalSwitchCardFooter,
  DocumentsContainer,
  DocumentsTitle,
  WithDate,
  WithoutDate,
  dropdownStyles,
  UserLoadingContainer,
  UserLoadingDisclaimer,
  MessageBarWrapper,
  TitleRow,
  SubTitleCol,
  SectionMargin,
  PromotionsListWrapper,
  PromotionTitle,
  SpinnerWithMargin,
  PromotionsLoadingRow,
  SourceFundFieldWrapper,
  SourceFundLabel,
  LoadingFundsWrapper,
  CardsRowWrapper,
  CardTitleIconsWrapper,
  SimularButtonWrapper,
  DocumentClickErrorBarWrapper,
  DocumentsIntroText,
  DocumentsSubtitle,
  LoadingDocsContainer,
  LoadingDocsText,
  CardsDisclaimerWrapper,
  CardsDisclaimerText,
  InstantCalculationTitle,
  EstimatedCalculationIntroText,
  AmountDisclaimerWrapper,
  AutoSwitchWarningWrapper,
} from "./InternalSwitchFormScreenStyles";
import { ReactComponent as DecrementIcon } from "@/images/decrement.svg";
import moment from "moment";
import DocPreLogService, {
  checkAllDocsIncluded,
  DocPreLogs,
} from "@/services/DocPreLogService";
import PromotionsService, {
  ValidateISINResponse,
} from "@/services/PromotionsService";
import UserService from "@/services/UserService";

interface RouteParams {}
interface IInternalSwitchFormScreenProps extends RouteComponentProps<RouteParams> {}

const amountTypeOptions: IChoiceGroupOption[] = [
  { key: "0", text: "Por importe", value: "amount" },
  { key: "1", text: "Por participaciones", value: "titles" },
  { key: "2", text: "Total", value: "total" },
];

// Al superar el 90% los fondos hacen operación por importe como si fuera total. Lo prevenimos.
const TOTAL_SWITCH_THRESHOLD = 0.8975;

export const InternalSwitchFormScreen: React.FunctionComponent<
  IInternalSwitchFormScreenProps
> = () => {
  const theme = useContext(ThemeContext);
  const { state } = useLocation<any>();
  const defaultOrigin = state?.origin;
  const defaultTarget = state?.target;
  const {
    handleSubmit,
    watch,
    formState: { errors },
    control,
    setValue,
    getValues,
    setError,
    clearErrors,
  } = useForm();
  const [portfolioFundsLoaded, setPortfolioFundsLoaded] = useState(false);
  const [portfolioFunds, setPortfolioFunds] = useState<any[]>([]);
  const [sending, setSending] = useState<boolean>(false);
  const [hasChangedIsin, setHasChangedIsin] = useState<boolean>(false);
  const history = useHistory();
  const mounted = useRef(false);
  const watchAmountType = watch("amountType", "");
  const watchAmount = watch("amount", "");
  const [user, setUser] = useState<any>(() => Auth.getUserProfile());
  const [userDataLoading, setUserDataLoading] = useState<boolean>(() => {
    const profile = Auth.getUserProfile();
    return !profile?.id || !profile?.mainUserIdentity?.id;
  });
  const [userLoadErrorMessage, setUserLoadErrorMessage] = useState<
    string | null
  >(null);
  let activePlan = Auth.getActivePlan();
  const outdated = Auth.isLastPlanOutdated();
  const outdatedContract = Auth.isLastPlanOutdatedContract();
  const cancelled = Auth.isActivePlanCancelled();
  const [simulation, setSimulation] = useState<boolean>(false);
  const [simulationLoading, setSimulationLoading] = useState<boolean>(false);
  const sourceFund = watch("sourceFund");
  const targetFund = watch("targetFund");
  const isSameSourceAndTarget =
    !!sourceFund?.isin &&
    !!targetFund?.isin &&
    sourceFund.isin.trim() === targetFund.isin.trim();
  const simulationAvailable =
    hasChangedIsin &&
    sourceFund !== undefined &&
    targetFund !== undefined &&
    !isSameSourceAndTarget;
  const [transferDates, setTransferDates] = useState<TransferEstimationDates>({
    compraLiquidacion: "",
    compraValor: "",
    finalProceso: "",
    ventaLiquidacion: "",
    ventaValor: "",
  });
  const [
    originTransferSimulationManagerInfo,
    setOriginTransferSimulationManagerInfo,
  ] = useState<TransferSimulationManagerInfo[]>([]);
  const [
    targetTransferSimulationManagerInfo,
    setTargetTransferSimulationManagerInfo,
  ] = useState<TransferSimulationManagerInfo[]>([]);
  const [selectedOriginPortfolioFund, setSelectedOriginPortfolioFund] =
    useState<any>();
  const [selectedOriginOptionKey, setSelectedOriginOptionKey] =
    useState<string>("");
  const [origenOptions, setOrigenOptions] = useState<IDropdownOption[]>();

  const [docPreLogs, setDocPreLogs] = useState<DocPreLogs[]>();
  const [loadingDocPreLogs, setLoadingDocPreLogs] = useState<boolean>(false);
  const [allDocumentsReaded, setAllDocumentsReaded] = useState<boolean>(true);
  const [targetFundDocuments, setTargetFundDocuments] = useState<any[]>([]);
  const [activePromotions, setActivePromotions] =
    useState<ValidateISINResponse | null>(null);
  const [loadingPromotions, setLoadingPromotions] = useState<boolean>(false);
  const [documentClickErrorMessage, setDocumentClickErrorMessage] = useState<
    string | null
  >(null);
  const [portfolioLoadErrorMessage, setPortfolioLoadErrorMessage] = useState<
    string | null
  >(null);
  const [orderErrorMessage, setOrderErrorMessage] = useState<string | null>(
    null,
  );
  const [targetFundData, setTargetFundData] = useState<{
    nav: number;
    ongoingCharges: number;
  } | null>(null);
  const [targetFundDataLoading, setTargetFundDataLoading] =
    useState<boolean>(false);
  const [targetFundSwitchable, setTargetFundSwitchable] = useState<
    boolean | null
  >(null);
  const [autoSwitchedToTotal, setAutoSwitchedToTotal] =
    useState<boolean>(false);

  const documentsColumns: any = [
    {
      key: "name",
      name: "Fichero",
      fieldName: "name",
      minWidth: 200,
      maxWidth: 300,
    },
    {
      key: "link",
      name: "Acción",
      fieldName: "url",
      minWidth: 120,
      maxWidth: 120,
    },
    {
      key: "dateClick",
      name: "Fecha de lectura",
      fieldName: "dateClick",
      minWidth: 180,
    },
  ];

  const onDocumentClick = (isin: string, document: any) => {
    setDocumentClickErrorMessage(null);
    DocPreLogService.createDocPreLogs({
      userId: user.id,
      isin: isin,
      documentType: document.type,
      dateClick: moment().toISOString(),
    })
      .then((response: DocPreLogs) => {
        setDocumentClickErrorMessage(null);
        setDocPreLogs((prevAux) => [...(prevAux ?? []), response]);
      })
      .catch(() => {
        setDocumentClickErrorMessage(
          "No se ha podido registrar la lectura del documento por un fallo. Por favor, inténtalo de nuevo.",
        );
      });
  };

  const renderDocumentColumn = (
    isin: string,
    item: any,
    index: any,
    column: any,
  ) => {
    switch (column.key) {
      case "name":
        if (docPreLogs === undefined) {
          return <></>;
        }
        return <span>{item.name}</span>;

      case "link":
        return (
          <a
            href={item.url}
            target="_blank"
            rel="noreferrer"
            onClick={() => onDocumentClick(isin, item)}
          >
            <i className={"ironia-icon downloads"} /> Descarga
          </a>
        );

      case "dateClick":
        if (docPreLogs === undefined) {
          return <></>;
        }
        var docPrelogDateClick = docPreLogs.find(
          (dPL) => dPL.isin === isin && dPL.documentType === item.type,
        );
        if (docPrelogDateClick) {
          return (
            <WithDate>
              <i className="ironia-icon circledCheck" />
              <span>
                {moment(docPrelogDateClick.dateClick).format(
                  "DD/MM/YYYY HH:mm",
                )}
              </span>
            </WithDate>
          );
        }

        return (
          <WithoutDate>
            <i className={`ironia-icon cross primaryColor`} />
            <span>
              Pulse sobre el enlace de descarga para leer el documento
            </span>
          </WithoutDate>
        );

      default:
        return item[column.fieldName];
    }
  };

  const loadFundDocuments = async (targetFund: any) => {
    if (targetFund && targetFund.isin) {
      try {
        const targetFundDetails = await fundService.getFund(targetFund.isin);
        setTargetFundDocuments(targetFundDetails.documents || []);
      } catch (error) {
        console.error("Error loading target fund documents:", error);
        setTargetFundDocuments([]);
      }
    } else {
      setTargetFundDocuments([]);
    }
  };

  const checkActivePromotions = async (targetFund: any) => {
    if (targetFund && targetFund.isin) {
      try {
        setLoadingPromotions(true);
        const promotions = await PromotionsService.checkActiveIsinPromotion([
          targetFund.isin,
        ]);
        setActivePromotions(promotions);
      } catch (error) {
        console.error("Error checking active promotions:", error);
        setActivePromotions(null);
      } finally {
        setLoadingPromotions(false);
      }
    } else {
      setActivePromotions(null);
      setLoadingPromotions(false);
    }
  };

  const loadPortfolioFunds = async () => {
    setPortfolioLoadErrorMessage(null);
    if (!activePlan?.portfolio?.finametrixId) {
      setPortfolioLoadErrorMessage(
        "No se han podido cargar los fondos de la cartera. Por favor, inténtelo de nuevo.",
      );
      return;
    }
    const currentDate = new Date();
    const yearStartDate = new Date(currentDate.getFullYear(), 0, 1);
    const fiscalItemsFrom = moment(yearStartDate).format("YYYYMMDD");
    const fiscalItemsTo = moment(currentDate).format("YYYYMMDD");

    const [finametrixPortfolio, fiscalItems] = await Promise.all([
      FinametrixService.getPortfolio(activePlan.portfolio.finametrixId).catch(
        () => {
          setPortfolioLoadErrorMessage(
            "No se han podido cargar los fondos de la cartera. Por favor, inténtelo de nuevo.",
          );
          return undefined;
        },
      ),
      FinametrixService.getFiscalItems(
        activePlan.portfolio.finametrixId,
        fiscalItemsFrom,
        fiscalItemsTo,
      ).catch(() => undefined),
    ]);

    if (typeof finametrixPortfolio?.creationDate !== "string") {
      return;
    }

    const fromDate = finametrixPortfolio.creationDate
      .substring(0, 10)
      .replace(/-/g, "");
    const date_to = function (this: any) {
      this.setDate(this.getDate() - 1);
      return this;
    }.call(new Date());

    const toDate =
      date_to.getFullYear().toString() +
      ("0" + (date_to.getMonth() + 1)).slice(-2) +
      ("0" + date_to.getDate()).slice(-2);

    const reporting = await FinametrixService.getReporting(
      activePlan.portfolio.finametrixId,
      fromDate,
      toDate,
    ).catch(() => {
      setPortfolioLoadErrorMessage(
        "No se han podido cargar los fondos de la cartera. Por favor, inténtalo de nuevo.",
      );
      return undefined;
    });
    if (
      !Array.isArray(reporting?.positions?.positionsTo?.positions) ||
      reporting.positions.positionsTo.positions.length <= 0
    ) {
      return;
    }

    var isins: string[] = [];
    for (let i = 0; i < reporting.positions.positionsTo.positions.length; i++) {
      let position = reporting.positions.positionsTo.positions[i];
      if (position.productType === 21) {
        isins.push(position.isin);
      }
    }
    var funds = await fundService.getFundsNameByIsin(isins);
    var aux: IDropdownOption[] = [];

    for (let i = 0; i < reporting.positions.positionsTo.positions.length; i++) {
      let position = reporting.positions.positionsTo.positions[i];
      if (
        position.productType === 21 &&
        funds?.find((x) => x.isin === position.isin)?.switchable
      ) {
        let plusMinus = 0;
        if (fiscalItems) {
          let isinFI = fiscalItems.fiscalItems.find(
            (x: any) => x.isin === position.isin,
          );
          if (isinFI && isinFI.fiscalItems) {
            plusMinus = isinFI.fiscalItems.reduce(
              (accumulator: any, current: { profit: any }) => {
                return accumulator + current.profit;
              },
              0,
            );
          }
        }

        let data = {
          isin: position.isin,
          name: position.productName,
          instrumentId: position.instrumentId,
          plusMinus: plusMinus,
          titles: position.titles,
          value: position.value,
          plusMinusPercentage: position.plusMinusPercentage,
          switchable: true,
        };
        setPortfolioFunds((prev) => [...prev, data]);

        let auxOption: IDropdownOption = {
          key: position.isin,
          text: position.isin + " | " + position.productName,
          selected: defaultOrigin?.isin === data?.isin ? true : false,
          id: position.isin.toString(),
        };
        aux.push(auxOption);

        if (defaultOrigin?.isin === data?.isin) {
          setSelectedOriginPortfolioFund(data);
          setSelectedOriginOptionKey("" + auxOption.key);
        }
      }
    }
    setOrigenOptions(aux);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (user?.id && user?.mainUserIdentity?.id) {
      setUserDataLoading(false);
      setUserLoadErrorMessage(null);
      return;
    }
    setUserLoadErrorMessage(null);
    const druId = user?.oid ?? user?.druId;
    if (user?.id) {
      UserService.getUser(user.id)
        .then((data) => {
          Auth.setUserProfile(data);
          setUser(Auth.getUserProfile());
        })
        .catch(() => {
          setUserLoadErrorMessage(
            "No se han podido cargar los datos del usuario. No es posible continuar con la operación.",
          );
        })
        .finally(() => {
          setUserDataLoading(false);
        });
      return;
    }
    if (druId) {
      UserService.getUserByDruid(druId)
        .then((data) => {
          Auth.setUserProfile(data);
          setUser(Auth.getUserProfile());
        })
        .catch(() => {
          setUserLoadErrorMessage(
            "No se han podido cargar los datos del usuario. No es posible continuar con la operación.",
          );
        })
        .finally(() => {
          setUserDataLoading(false);
        });
      return;
    }
    setUserDataLoading(false);
    setUserLoadErrorMessage(
      "No se han podido cargar los datos del usuario. No es posible continuar con la operación.",
    );
  }, [user?.id, user?.oid, user?.druId, user?.mainUserIdentity?.id]);

  useEffect(() => {
    const values = getValues();
    if (defaultOrigin && defaultOrigin !== values.sourceFund) {
      setValue("sourceFund", defaultOrigin);
    }
    if (defaultTarget && defaultTarget !== values.targetFund) {
      setValue("targetFund", defaultTarget);
    }

    loadPortfolioFunds().finally(() => {
      setTimeout(() => setPortfolioFundsLoaded(true));
    });

    mounted.current = true;
    return () => {
      mounted.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (targetFund) {
      loadFundDocuments(targetFund);
    }
  }, [targetFund]);

  useEffect(() => {
    if (targetFund?.isin) {
      setLoadingDocPreLogs(true);
      DocPreLogService.docPreLogsByUserId(user.id, [targetFund.isin]).then(
        (response: DocPreLogs[]) => {
          setDocPreLogs(response);
          setTimeout(() => setLoadingDocPreLogs(false), 100);
        },
      );
    } else {
      setDocPreLogs([]);
      setLoadingDocPreLogs(false);
    }
  }, [targetFund?.isin, user.id]);

  useEffect(() => {
    let allRead = false;

    if (
      docPreLogs !== undefined &&
      targetFund &&
      targetFundDocuments.length > 0
    ) {
      const docPreLogsTypesByTargetIsin = docPreLogs
        .filter((dPL: DocPreLogs) => dPL.isin === targetFund.isin)
        .map((dPL: any) => dPL.documentType);
      const docsIncluded = checkAllDocsIncluded(
        targetFund.isin,
        targetFundDocuments,
      );
      const hasUnreadTarget =
        !docsIncluded.result ||
        docsIncluded.documents.some(
          (d: any) => !docPreLogsTypesByTargetIsin.includes(d.type),
        );

      if (!hasUnreadTarget) {
        allRead = true;
      }
    }
    setAllDocumentsReaded(allRead);
  }, [docPreLogs, targetFundDocuments, targetFund]);

  useEffect(() => {
    if (targetFund && targetFund.isin) {
      checkActivePromotions(targetFund);
    } else if (targetFund === undefined) {
      setActivePromotions(null);
      setLoadingPromotions(false);
    }
  }, [targetFund]);

  useEffect(() => {
    if (!targetFund?.isin) {
      setTargetFundData(null);
      setTargetFundSwitchable(null);
      setTargetFundDataLoading(false);
      return;
    }
    setTargetFundSwitchable(null);
    setTargetFundDataLoading(true);
    const isin = targetFund.isin;
    Promise.all([
      fundService.getPortfolioModelFullInformation([isin]),
      fundService.getInfoFundsPortfolio([isin]),
    ])
      .then(([navResponse, infoResponse]) => {
        const navList = Array.isArray(navResponse) ? navResponse : [];
        const infoList = Array.isArray(infoResponse) ? infoResponse : [];
        const navItem =
          navList.find((x: any) => x?.isin === isin) ?? navList[0];
        const infoItem =
          infoList.find((x: any) => x?.isin === isin) ?? infoList[0];
        const nav =
          navItem != null && typeof navItem.vLiq === "number"
            ? navItem.vLiq
            : 0;
        const ongoingCharges =
          infoItem != null && typeof infoItem.ongoingCharges === "number"
            ? infoItem.ongoingCharges
            : 0;
        setTargetFundData({ nav, ongoingCharges });
        setTargetFundSwitchable(
          infoItem != null && typeof infoItem.switchable === "boolean"
            ? infoItem.switchable
            : null,
        );
      })
      .catch(() => {
        setTargetFundData(null);
        setTargetFundSwitchable(null);
      })
      .finally(() => {
        setTargetFundDataLoading(false);
      });
  }, [targetFund?.isin]);

  useEffect(() => {
    if (
      !selectedOriginPortfolioFund ||
      (watchAmountType !== "titles" && watchAmountType !== "amount")
    ) {
      return;
    }
    const amountNum = parseFloat(watchAmount);
    if (isNaN(amountNum) || amountNum <= 0) {
      return;
    }
    const totalTitles = selectedOriginPortfolioFund.titles ?? 0;
    const totalValue = selectedOriginPortfolioFund.value ?? 0;

    if (watchAmountType === "titles") {
      if (amountNum >= totalTitles) {
        setValue("amountType", "total");
        setValue("amount", "");
        setAutoSwitchedToTotal(false);
      }
    } else if (watchAmountType === "amount") {
      if (amountNum >= totalValue) {
        setValue("amountType", "total");
        setValue("amount", "");
        setAutoSwitchedToTotal(false);
      } else if (
        totalValue > 0 &&
        amountNum >= totalValue * TOTAL_SWITCH_THRESHOLD
      ) {
        setValue("amountType", "total");
        setValue("amount", "");
        setAutoSwitchedToTotal(true);
      }
    }
  }, [watchAmount, watchAmountType, selectedOriginPortfolioFund, setValue]);

  const handleSimulation = () => {
    setSimulationLoading(true);
    setSimulation(true);
    const fundOrigen = watch("sourceFund");
    const fundDestino = watch("targetFund");

    if (fundOrigen !== undefined && fundDestino !== undefined) {
      fundService
        .GetTransferSimulationManagerInfo([fundOrigen.isin], [])
        .then((response: TransferSimulationManagerInfo[]) =>
          setOriginTransferSimulationManagerInfo(response),
        );
      fundService
        .GetTransferSimulationManagerInfo([fundDestino.isin], [])
        .then((response: TransferSimulationManagerInfo[]) =>
          setTargetTransferSimulationManagerInfo(response),
        );
      fundService
        .getTransferEstimationDates(fundOrigen.isin, fundDestino.isin, true)
        .then((response: any) => {
          if (response !== null && response !== undefined) {
            setTransferDates({
              ventaValor: response.ventaValor,
              ventaLiquidacion: response.ventaLiquidacion,
              compraValor: response.compraValor,
              compraLiquidacion: response.compraLiquidacion,
              finalProceso: response.finalProceso,
            });
          } else {
            setSimulation(false);
          }
        })
        .finally(() => {
          setSimulationLoading(false);
        });
    } else {
      setSimulation(false);
      setSimulationLoading(false);
    }
  };
  const onSubmit = (data: any) => {
    if (data.sourceFund?.instrumentId === undefined) {
      setError("sourceFund", {
        type: "invalidOriginFund",
        message: "El fondo de origen no es válido",
      });
      return false;
    }

    if(isSameSourceAndTarget)
    {
      return false;
    }

    if (data.amountType === "total" && typeof data.amount !== "undefined") {
      delete data.amount;
    }

    let toSend: any = {
      InstrumentId: data.sourceFund?.instrumentId,
      UserIdentityId: user.mainUserIdentity.id,
      FnmPortfolioId: activePlan?.portfolio?.id,
      OrderType: 2,
      Values: {
        FundName: data.sourceFund?.name,
        FundIsin: data.sourceFund?.isin.trim(),
        Fund: data.sourceFund?.instrumentId,
        AmountType: data.amountType,
        Amount: data.amount !== undefined ? parseFloat(data.amount) : 0,
        NewInstrument: data.targetFund?.finametrixId,
        NewInstrumentName: data.targetFund?.name,
        Type: data.amountType,
      },
    };

    setOrderErrorMessage(null);
    setSending(true);
    OrderService.createOrder(toSend)
      .then((response: OrderType) => {
        if (response?.id) {
          history.push("/internal-switch/signature", {
            signature: response.signature,
            transfer: response,
            isinOrigin: data.sourceFund.isin.trim(),
            isinDestination: data.targetFund.isin.trim(),
          });
        } else {
          setOrderErrorMessage(
            "No se ha podido completar la orden. Por favor, inténtelo de nuevo o contacte con soporte.",
          );
        }
      })
      .catch((error) => {
        if (error.response?.status === 409) {
          history.push("/UpgradeSubscription", {
            limitExceeded: error.response.data,
          });
        }
        else if(error.response?.status === 400 && error.response.data?.code === "SAME_SOURCE_AND_TARGET_FUND")
        {
          setError("targetFund", {
            type: "sameSourceAndTarget",
            message: error.response.data.message,
          });
        }
        else if(error.response?.status === 400 && error.response.data?.message)
        {
          setOrderErrorMessage(error.response.data.message);
        }
        else
        {
          setOrderErrorMessage(
            "No se ha podido crear la orden. Por favor, inténtalo de nuevo.",
          );
        }
      })
      .finally(() => {
        if (mounted.current) {
          setSending(false);
        }
      });
  };

  if (outdated === true || cancelled === true || outdatedContract === true) {
    return <Redirect to="/savingsPlans" />;
  }

  if (userDataLoading) {
    return (
      <L.Main theme={theme}>
        <UserLoadingContainer>
          <Spinner size={SpinnerSize.large} />
          <span>Cargando datos del usuario...</span>
          <UserLoadingDisclaimer>
            No podrá realizar ninguna operación hasta que se carguen sus datos.
          </UserLoadingDisclaimer>
        </UserLoadingContainer>
      </L.Main>
    );
  }

  if (userLoadErrorMessage) {
    return (
      <L.Main theme={theme}>
        <L.SeparatorTop />
        <MessageBarWrapper>
          <MessageBar messageBarType={MessageBarType.error} isMultiline={true}>
            {userLoadErrorMessage}
          </MessageBar>
        </MessageBarWrapper>
      </L.Main>
    );
  }

  if (!user?.id || !user?.mainUserIdentity?.id) {
    return null;
  }

  return (
    <React.Fragment>
      <DocumentTitle title={"IronIA - Traspaso Interno"}>
        <form
          className="internal-transfer-form"
          id="internal-transfer-form"
          onSubmit={handleSubmit(onSubmit)}
          noValidate
        >
          <L.Main theme={theme}>
            {activePlan !== undefined && (
              <TitleRow className="ms-Grid-row">
                <div className="ms-Grid-col">
                  <L.TitleContainer>
                    <h2>Realiza un traspaso interno</h2>
                  </L.TitleContainer>
                </div>
                <SubTitleCol className="ms-Grid-col">
                  <L.SubTitleContainer> {activePlan.name}</L.SubTitleContainer>
                </SubTitleCol>
              </TitleRow>
            )}
            {activePlan === undefined && (
              <L.TitleContainer>
                <h2>Realiza un traspaso interno</h2>
              </L.TitleContainer>
            )}
            <L.SeparatorTop />
            {portfolioLoadErrorMessage && (
              <MessageBarWrapper>
                <MessageBar
                  messageBarType={MessageBarType.error}
                  onDismiss={() => setPortfolioLoadErrorMessage(null)}
                  dismissButtonAriaLabel="Cerrar"
                >
                  {portfolioLoadErrorMessage}
                </MessageBar>
              </MessageBarWrapper>
            )}
            {orderErrorMessage && (
              <MessageBarWrapper>
                <MessageBar
                  messageBarType={MessageBarType.error}
                  onDismiss={() => setOrderErrorMessage(null)}
                  dismissButtonAriaLabel="Cerrar"
                >
                  {orderErrorMessage}
                </MessageBar>
              </MessageBarWrapper>
            )}
            {!loadingPromotions &&
              activePromotions &&
              (activePromotions as any).TotalPromotionsFound > 0 && (
                <PromotionsListWrapper>
                  {((activePromotions as any).AvailablePromotions || []).map(
                    (promotion: any, index: number) => (
                      <MessageBar
                        key={`promotion-top-${index}`}
                        messageBarType={MessageBarType.success}
                        isMultiline={true}
                      >
                        <PromotionTitle>
                          ¡Promoción activa! - {promotion.Name} para el ISIN:{" "}
                          {promotion.ISIN}
                        </PromotionTitle>
                      </MessageBar>
                    ),
                  )}
                </PromotionsListWrapper>
              )}
            {loadingPromotions && (
              <SectionMargin>
                <MessageBar
                  messageBarType={MessageBarType.info}
                  isMultiline={false}
                >
                  <PromotionsLoadingRow>
                    <SpinnerWithMargin>
                      <Spinner size={SpinnerSize.small} />
                    </SpinnerWithMargin>
                    <span>Verificando si hay promociones disponibles...</span>
                  </PromotionsLoadingRow>
                </MessageBar>
              </SectionMargin>
            )}
            <div className="ms-Grid restarted-ms-Grid">
              <div className="ms-Grid-row">
                <div className="ms-Grid-col ms-sm12 ms-xl6">
                  <L.CardContainer theme={theme}>
                    <L.CardHeader>
                      <L.CardHeadingContainer>
                        <h3>Datos origen</h3>
                      </L.CardHeadingContainer>
                    </L.CardHeader>
                    <L.CardBody>
                      <Stack>
                        <Stack.Item>
                          <Controller
                            control={control}
                            name="sourceFund"
                            rules={{ required: true }}
                            render={({ field, field: { onChange } }) => (
                              <SourceFundFieldWrapper>
                                <SourceFundLabel>
                                  Fondo de Origen *
                                </SourceFundLabel>
                                <Dropdown
                                  {...field}
                                  placeholder={"Selecciona un fondo de origen"}
                                  options={origenOptions as IDropdownOption[]}
                                  selectedKey={selectedOriginOptionKey}
                                  onChange={(ev: any, option: any) => {
                                    if (option !== undefined) {
                                      setHasChangedIsin(true);
                                      setSimulation(false);
                                      setSelectedOriginPortfolioFund(
                                        portfolioFunds.find(
                                          (x) => x.isin === option.key,
                                        ),
                                      );
                                      setSelectedOriginOptionKey(option.key);
                                      let thisPortfolioFund =
                                        portfolioFunds.find(
                                          (x) => x.isin === option.key,
                                        );
                                      if (thisPortfolioFund) {
                                        setValue(
                                          "sourceFund",
                                          thisPortfolioFund,
                                        );
                                        onChange({
                                          target: {
                                            value: thisPortfolioFund,
                                            name: thisPortfolioFund.name,
                                          },
                                        });
                                      }
                                    } else {
                                      setValue("sourceFund", undefined);
                                      setSelectedOriginPortfolioFund(undefined);
                                      setSelectedOriginOptionKey("");
                                      setHasChangedIsin(false);
                                      onChange({
                                        target: {
                                          value: undefined,
                                          name: "sourceFund",
                                        },
                                      });
                                    }
                                  }}
                                  styles={dropdownStyles}
                                  disabled={!portfolioFundsLoaded}
                                  errorMessage={
                                    errors.sourceFund &&
                                    ((typeof errors.sourceFund.type ===
                                      "string" &&
                                      errors.sourceFund.type === "required" &&
                                      "Debe seleccionar un fondo de origen.") ||
                                      "El fondo de origen no es válido.")
                                  }
                                />
                              </SourceFundFieldWrapper>
                            )}
                          />
                        </Stack.Item>
                      </Stack>
                    </L.CardBody>
                    {!selectedOriginPortfolioFund && !portfolioFundsLoaded && (
                      <React.Fragment>
                        <LoadingFundsWrapper>
                          <Spinner
                            size={SpinnerSize.medium}
                            className="button-spinner"
                          />
                          &nbsp;&nbsp; Cargando información de los fondos de tu
                          cartera
                        </LoadingFundsWrapper>
                      </React.Fragment>
                    )}
                    {selectedOriginPortfolioFund && portfolioFundsLoaded && (
                      <CardsRowWrapper>
                        <InternalSwitchCardContainer theme={theme}>
                          <InternalSwitchCardTitle>
                            Importe
                            <CardTitleIconsWrapper>
                              {selectedOriginPortfolioFund?.plusMinusPercentage >
                                0 && <IncrementIcon />}
                              {selectedOriginPortfolioFund?.plusMinusPercentage <
                                0 && <DecrementIcon />}
                            </CardTitleIconsWrapper>
                          </InternalSwitchCardTitle>
                          <InternalSwitchCardContent>
                            {currencyFormatter.format(
                              selectedOriginPortfolioFund?.value ?? 0,
                            )}
                          </InternalSwitchCardContent>
                        </InternalSwitchCardContainer>
                        <InternalSwitchCardContainer theme={theme}>
                          <InternalSwitchCardTitle>
                            Títulos / Participaciones
                          </InternalSwitchCardTitle>
                          <InternalSwitchCardContent>
                            {decimalFormatter.format(
                              selectedOriginPortfolioFund?.titles ?? 0,
                            )}
                          </InternalSwitchCardContent>
                        </InternalSwitchCardContainer>
                        <InternalSwitchCardContainer theme={theme}>
                          <InternalSwitchCardTitle>
                            Plusvalía / Minusvalía
                          </InternalSwitchCardTitle>
                          <InternalSwitchCardContent>
                            {currencyFormatter.format(
                              selectedOriginPortfolioFund?.plusMinus ?? 0,
                            )}
                          </InternalSwitchCardContent>
                          <InternalSwitchCardFooter>
                            * Latente
                          </InternalSwitchCardFooter>
                        </InternalSwitchCardContainer>
                      </CardsRowWrapper>
                    )}
                    <L.SeparatorForm />
                    <h3>Tipo de importe</h3>
                    <Stack>
                      <Stack.Item>
                        <Controller
                          name="amountType"
                          control={control}
                          rules={{ required: true }}
                          render={({ field }) => (
                            <ChoiceGroup
                              {...field}
                              required
                              options={amountTypeOptions}
                              selectedKey={
                                amountTypeOptions.find(
                                  (opt) => opt.value === field.value,
                                )?.key ?? undefined
                              }
                              onChange={(ev, option) => {
                                if (option) {
                                  setAutoSwitchedToTotal(false);
                                  field.onChange(option.value);
                                }
                              }}
                            />
                          )}
                        />
                      </Stack.Item>
                      {watchAmountType === "amount" && (
                        <Stack.Item>
                          <AmountDisclaimerWrapper>
                            <p>
                              Si el traspaso es por un importe superior al 90%
                              de tu posición en el fondo, para evitar
                              descubiertos por posibles fluctuaciones del valor
                              liquidativo, la operación se tramitará por la
                              totalidad de las participaciones en el fondo. Si
                              no deseas que la operación se tramite así, puedes
                              realizarla por las participaciones exactas que
                              desees.
                            </p>
                          </AmountDisclaimerWrapper>
                        </Stack.Item>
                      )}
                      {autoSwitchedToTotal && (
                        <Stack.Item>
                          <AutoSwitchWarningWrapper>
                            <MessageBar
                              messageBarType={MessageBarType.warning}
                              isMultiline={true}
                              styles={{ root: { cursor: "default" } }}
                            >
                              <strong>¡ATENCIÓN!</strong> Hemos cambiado
                              automáticamente el tipo de operación a{" "}
                              <strong>Total</strong> porque el importe
                              introducido supera el 90% de tu posición en el
                              fondo.
                            </MessageBar>
                          </AutoSwitchWarningWrapper>
                        </Stack.Item>
                      )}
                      {watchAmountType !== "total" && (
                        <Stack.Item>
                          <Controller
                            name="amount"
                            control={control}
                            rules={{
                              required: true,
                              min: 0 + Number.MIN_VALUE,
                            }}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                required
                                type="number"
                                label={
                                  (watchAmountType === "titles" &&
                                    "Participaciones") ||
                                  "Importe"
                                }
                                placeholder={
                                  (watchAmountType === "titles" &&
                                    "Escribe aquí el número de participaciones...") ||
                                  "Escribe aquí el importe..."
                                }
                                errorMessage={
                                  errors.amount &&
                                  ((watchAmountType === "titles" &&
                                    ((typeof errors.amount.type === "string" &&
                                      errors.amount.type === "required" &&
                                      "Indique las participaciones a traspasar.") ||
                                      (typeof errors.amount.type === "string" &&
                                        errors.amount.type === "min" &&
                                        "Debe indicar un número mayor de participaciones.") ||
                                      "Introduzca un número de participaciones válido.")) ||
                                    (typeof errors.amount.type === "string" &&
                                      errors.amount.type === "required" &&
                                      "Debe indicar el importe a traspasar.") ||
                                    (typeof errors.amount.type === "string" &&
                                      errors.amount.type === "min" &&
                                      "El importe debe ser mayor.") ||
                                    "Introduzca un importe válido.")
                                }
                              />
                            )}
                          />
                        </Stack.Item>
                      )}
                    </Stack>
                  </L.CardContainer>
                </div>
                <div className="ms-Grid-col ms-sm12 ms-xl6">
                  <L.CardContainer theme={theme}>
                    <L.CardHeader>
                      <L.CardHeadingContainer>
                        <h3>Datos destino</h3>
                      </L.CardHeadingContainer>
                    </L.CardHeader>
                    <L.CardBody>
                      <Stack>
                        <Stack.Item>
                          <Controller
                            name="targetFund"
                            control={control}
                            rules={{ required: true }}
                            render={({ field, field: { onChange } }) => (
                              <InvestmentFundFinder
                                {...field}
                                required
                                labelISIN="ISIN de destino"
                                labelName="Nombre del fondo de destino"
                                placeholderISIN="Escribe aquí el ISIN de destino..."
                                placeholderName="Escribe aquí el nombre del fondo de destino..."
                                default={defaultTarget}
                                loadData={(filter: any) => {
                                  return fundService
                                    .getAvailableFunds(filter)
                                    .then((data) => data)
                                    .catch((err) => []);
                                }}
                                onChange={(ev: any, name: any, value: any) => {
                                  if (value !== undefined) {
                                    setHasChangedIsin(true);
                                    setSimulation(false);
                                  } else {
                                    setHasChangedIsin(false);
                                  }
                                  clearErrors("targetFund");
                                  setValue(name, value);
                                  onChange({ target: { value, name } });
                                }}
                                errorMessage={
                                  (isSameSourceAndTarget &&
                                    "El fondo de destino no puede ser el mismo que el de origen.") ||
                                  (errors.targetFund &&
                                  ((errors.targetFund.type === "sameSourceAndTarget" &&
                                    errors.targetFund.message) ||
                                    (typeof errors.targetFund.type ===
                                    "string" &&
                                    errors.targetFund.type === "required" &&
                                    "Debe seleccionar un fondo de destino.") ||
                                    "El fondo de destino no es válido."))
                                }
                              />
                            )}
                          />
                        </Stack.Item>
                      </Stack>
                      {targetFund &&
                        (targetFundData != null || targetFundDataLoading) &&
                        (() => {
                          if (targetFundDataLoading) {
                            return (
                              <LoadingFundsWrapper>
                                <Spinner
                                  size={SpinnerSize.medium}
                                  className="button-spinner"
                                />
                              </LoadingFundsWrapper>
                            );
                          }
                          if (!targetFundData) {
                            return null;
                          }
                          const totalTitles =
                            selectedOriginPortfolioFund?.titles ?? 0;
                          const totalValue =
                            selectedOriginPortfolioFund?.value ?? 0;
                          let operationAmountX: number = 0;
                          if (selectedOriginPortfolioFund) {
                            if (watchAmountType === "total") {
                              operationAmountX = totalValue;
                            } else if (watchAmountType === "amount") {
                              const amountNum = parseFloat(watchAmount);
                              if (!isNaN(amountNum) && amountNum > 0) {
                                operationAmountX = amountNum;
                              }
                            } else if (watchAmountType === "titles") {
                              const amountNum = parseFloat(watchAmount);
                              if (
                                !isNaN(amountNum) &&
                                amountNum > 0 &&
                                totalTitles > 0
                              ) {
                                operationAmountX =
                                  amountNum * (totalValue / totalTitles);
                              }
                            }
                          }
                          const nav = targetFundData.nav;
                          const ongoingCharges = targetFundData.ongoingCharges;
                          const estimatedTitles =
                            nav > 0
                              ? roundToDecimalPlaces(operationAmountX / nav, 4)
                              : 0;
                          const ongoingChargesPercent =
                            typeof ongoingCharges === "number"
                              ? ongoingCharges
                              : 0;
                          const chargesAmount =
                            operationAmountX * (ongoingChargesPercent / 100);
                          return (
                            <React.Fragment>
                              <InstantCalculationTitle>
                                Cálculo Estimado
                              </InstantCalculationTitle>
                              <EstimatedCalculationIntroText>
                                Estos valores se realizan estimando el cálculo
                                al tomar el último valor del precio del fondo de
                                destino.
                              </EstimatedCalculationIntroText>
                              <CardsRowWrapper>
                                <InternalSwitchCardContainer theme={theme}>
                                  <InternalSwitchCardTitle>
                                    Importe
                                  </InternalSwitchCardTitle>
                                  <InternalSwitchCardContent>
                                    {currencyFormatter.format(operationAmountX)}
                                  </InternalSwitchCardContent>
                                </InternalSwitchCardContainer>
                                <InternalSwitchCardContainer theme={theme}>
                                  <InternalSwitchCardTitle>
                                    Títulos / Participaciones
                                  </InternalSwitchCardTitle>
                                  <InternalSwitchCardContent>
                                    {decimalFormatter.format(estimatedTitles)}
                                  </InternalSwitchCardContent>
                                  <InternalSwitchCardFooter>
                                    * Estimadas en destino
                                  </InternalSwitchCardFooter>
                                </InternalSwitchCardContainer>
                                <InternalSwitchCardContainer theme={theme}>
                                  <InternalSwitchCardTitle>
                                    Gastos Corrientes
                                  </InternalSwitchCardTitle>
                                  <InternalSwitchCardContent>
                                    {ongoingChargesPercent > 0
                                      ? `${decimalFormatter.format(ongoingChargesPercent)} %`
                                      : "-"}
                                  </InternalSwitchCardContent>
                                  <InternalSwitchCardFooter>
                                    {ongoingChargesPercent > 0
                                      ? `* Equivale a ${currencyFormatter.format(chargesAmount)}`
                                      : "* Del fondo destino"}
                                  </InternalSwitchCardFooter>
                                </InternalSwitchCardContainer>
                              </CardsRowWrapper>
                            </React.Fragment>
                          );
                        })()}
                    </L.CardBody>
                  </L.CardContainer>
                </div>
              </div>
            </div>

            <L.OuterFooterArea>
              <L.CardButton
                size="lg"
                type="button"
                onClick={() => history.goBack()}
              >
                Volver
              </L.CardButton>
              <L.FooterRightButtons>
                <SimularButtonWrapper>
                  <L.CardButton
                    size="lg"
                    disabled={!simulationAvailable}
                    type="button"
                    onClick={() => {
                      setSimulationLoading(true);
                      handleSimulation();
                    }}
                  >
                    Simular
                  </L.CardButton>
                </SimularButtonWrapper>
                <L.CardButton
                  size="lg"
                  disabled={
                    sending ||
                    !selectedOriginPortfolioFund ||
                    isSameSourceAndTarget ||
                    !allDocumentsReaded
                  }
                >
                  {sending && (
                    <Spinner
                      size={SpinnerSize.small}
                      className="button-spinner"
                    />
                  )}
                  Continuar
                </L.CardButton>
              </L.FooterRightButtons>
            </L.OuterFooterArea>
            {simulation === true &&
              simulationAvailable === true &&
              transferDates !== undefined && (
                <TransferTimeLine
                  isLoadingProps={simulationLoading}
                  transferEstimationDates={transferDates}
                  simulationManagerInfoTarget={
                    targetTransferSimulationManagerInfo
                  }
                  simulationManagerInfoOrigin={
                    originTransferSimulationManagerInfo
                  }
                />
              )}
            {/* Aviso de fondo no traspasable */}
            {targetFund &&
              !targetFundDataLoading &&
              targetFundSwitchable === false && (
                <CardsDisclaimerWrapper theme={theme}>
                  <CardsDisclaimerText>
                    Estás realizando voluntariamente un traspaso al fondo{" "}
                    {targetFund.name} - {targetFund.isin} que NO ES TRASPASABLE.
                  </CardsDisclaimerText>
                </CardsDisclaimerWrapper>
              )}
            {/* Disclaimer Cálculo Estimado */}
            {targetFund && targetFundData && !targetFundDataLoading && (
              <CardsDisclaimerWrapper theme={theme}>
                <CardsDisclaimerText>
                  Los datos del Cálculo Estimado son orientativos y no tienen
                  carácter vinculante legalmente para reclamación ni reflejan
                  necesariamente los valores finales una vez ejecutada la
                  operación en el mercado.
                </CardsDisclaimerText>
              </CardsDisclaimerWrapper>
            )}
            {/* Sección de documentos obligatorios */}
            {!loadingDocPreLogs && !allDocumentsReaded && (
              <DocumentsContainer theme={theme}>
                <DocumentsTitle>
                  Documentación obligatoria de IIC's
                </DocumentsTitle>
                {documentClickErrorMessage && (
                  <DocumentClickErrorBarWrapper>
                    <MessageBar
                      messageBarType={MessageBarType.error}
                      onDismiss={() => setDocumentClickErrorMessage(null)}
                      dismissButtonAriaLabel="Cerrar"
                    >
                      {documentClickErrorMessage}
                    </MessageBar>
                  </DocumentClickErrorBarWrapper>
                )}
                <DocumentsIntroText>
                  De conformidad con lo establecido en la normativa vigente
                  aplicable a Instituciones de Inversión Colectiva y en las
                  Circulares 4/2008 y 2/2011 de la Comisión Nacional del Mercado
                  de Valores, debe leer la documentación legal requerida antes
                  de realizar el traspaso.
                </DocumentsIntroText>
                <div className={`ms-Grid-row ${styles.documents}`}>
                  {watch("targetFund") &&
                    (() => {
                      const allDocsReaded = checkAllDocsIncluded(
                        watch("targetFund").isin,
                        targetFundDocuments,
                      );
                      return (
                        <div className={`ms-Grid-col ms-lg12 ms-xl6 p10`}>
                          <DocumentsSubtitle>
                            Documentos del fondo destino -{" "}
                            {watch("targetFund").isin}
                          </DocumentsSubtitle>
                          {!allDocsReaded.result ? (
                            <p className={`shadow ${styles.noDocuments}`}>
                              <i
                                className={`ironia-icon cross inlineBlock ${styles.cross}`}
                              />
                              <span>
                                No hay documentación disponible para este fondo
                                por lo que no se puede usar como destino de un
                                traspaso.
                              </span>
                            </p>
                          ) : (
                            <DetailsList
                              setKey="targetDocs"
                              items={allDocsReaded.documents}
                              columns={documentsColumns}
                              onRenderItemColumn={(row, index, column) =>
                                renderDocumentColumn(
                                  watch("targetFund").isin,
                                  row,
                                  index,
                                  column,
                                )
                              }
                              selectionMode={SelectionMode.none}
                            />
                          )}
                        </div>
                      );
                    })()}
                </div>
              </DocumentsContainer>
            )}
            {loadingDocPreLogs && (
              <DocumentsContainer theme={theme}>
                <LoadingDocsContainer>
                  <Spinner size={SpinnerSize.medium} />
                  <LoadingDocsText>Cargando documentación...</LoadingDocsText>
                </LoadingDocsContainer>
              </DocumentsContainer>
            )}
          </L.Main>
        </form>
      </DocumentTitle>
    </React.Fragment>
  );
};
